import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ArrowLeft,
  Printer,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import BlurredValue from '../../../components/common/BlurredValue';
import { formatDateToISO } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const WholesaleBalanceSheetPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [reportType, setReportType] = useState('current');
  const [asOfDate, setAsOfDate] = useState(formatDateToISO(new Date()));
  const [compareEnabled, setCompareEnabled] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [reportType]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken');

      let url;
      switch (reportType) {
        case 'current':
          url = `${API_URL}/reports/balance-sheet/current?compare=${compareEnabled}&module=wholesale`;
          break;
        case 'custom':
          if (!asOfDate) {
            setError('Please select a date');
            setLoading(false);
            return;
          }
          url = `${API_URL}/reports/balance-sheet?as_of_date=${asOfDate}&compare=${compareEnabled}&module=wholesale`;
          break;
        case 'month-end':
          const date = new Date(asOfDate);
          url = `${API_URL}/reports/balance-sheet/month-end/${date.getFullYear()}/${date.getMonth() + 1}?compare=${compareEnabled}&module=wholesale`;
          break;
        case 'year-end':
          const year = new Date(asOfDate).getFullYear();
          url = `${API_URL}/reports/balance-sheet/year-end/${year}?compare=${compareEnabled}&module=wholesale`;
          break;
        default:
          url = `${API_URL}/reports/balance-sheet/current?compare=${compareEnabled}&module=wholesale`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReportData(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch wholesale balance sheet');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderVariance = (variance) => {
    if (!variance) return null;

    const isPositive = variance.amount >= 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';

    return (
      <div className={`flex items-center gap-1 ${colorClass} text-sm`}>
        <Icon className="h-4 w-4" />
        <span>{formatCurrency(Math.abs(variance.amount))}</span>
        <span>({formatPercentage(variance.percentage)})</span>
      </div>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 print:hidden">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/wholesale/reports')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Wholesale Balance Sheet</h1>
              <p className="text-sm text-gray-500">
                Statement of Financial Position - Wholesale Module
              </p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="text-white px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="current">Current (Today)</option>
                <option value="month-end">Month End</option>
                <option value="year-end">Year End</option>
                <option value="custom">Custom Date</option>
              </select>
            </div>

            {(reportType === 'custom' ||
              reportType === 'month-end' ||
              reportType === 'year-end') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {reportType === 'year-end' ? 'Year' : 'As of Date'}
                </label>
                <input
                  type="date"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            )}

            <div className="flex items-end">
              <button
                onClick={fetchReport}
                className="w-full text-white px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
              >
                Generate Report
              </button>
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={compareEnabled}
                onChange={(e) => setCompareEnabled(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Compare with Previous Year</span>
            </label>
          </div>
        </div>

        {/* Print Header */}
        <div className="hidden print:block mb-6">
          <h1 className="text-2xl font-bold text-gray-900 text-center">Wholesale Balance Sheet</h1>
          <p className="text-sm text-gray-600 text-center">
            As of {formatDate(reportData?.as_of_date)}
          </p>
          <p className="text-sm text-gray-600 text-center">Wholesale Module Report</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 print:hidden">
            {error}
          </div>
        )}

        {reportData && (
          <>
            {/* Accounting Equation Check */}
            <div
              className={`rounded-lg p-4 mb-6 ${reportData.current.is_balanced ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
            >
              <div className="flex items-center gap-2">
                {reportData.current.is_balanced ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      Books are in balance: Assets = Liabilities + Equity
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium text-red-800">
                      Warning: Books are out of balance by{' '}
                      {formatCurrency(reportData.current.accounting_equation_check.difference)}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Financial Ratios */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 print:hidden">
              <div className="bg-white rounded-lg shadow p-4 border-2 border-emerald-500">
                <div className="text-sm text-gray-500">Current Ratio</div>
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                  {reportData.current.ratios.current_ratio.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Current Assets / Current Liabilities
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-500">
                <div className="text-sm text-gray-500">Quick Ratio</div>
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                  {reportData.current.ratios.quick_ratio.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-1">Liquidity Measure</div>
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-purple-500">
                <div className="text-sm text-gray-500">Debt-to-Equity</div>
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                  {reportData.current.ratios.debt_to_equity_ratio.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-1">Financial Leverage</div>
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-amber-500">
                <div className="text-sm text-gray-500">Working Capital</div>
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-emerald-600">
                  <BlurredValue value={formatCurrency(reportData.current.ratios.working_capital)} />
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Current Assets - Current Liabilities
                </div>
              </div>
            </div>

            {/* Balance Sheet Report */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Account
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      {reportData.comparison && (
                        <>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Previous Year
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Variance
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* ASSETS */}
                    <tr className="bg-emerald-50">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-3 text-sm font-bold text-gray-900"
                      >
                        ASSETS
                      </td>
                    </tr>

                    {/* Current Assets */}
                    <tr className="bg-gray-50">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-2 text-sm font-semibold text-gray-900"
                      >
                        Current Assets
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                        Cash and Bank Accounts
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.current.assets.current_assets.cash)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.assets.current_assets.cash)}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">Accounts Receivable</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(
                          reportData.current.assets.current_assets.accounts_receivable
                        )}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.assets.current_assets.accounts_receivable
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">Inventory</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        <BlurredValue
                          value={formatCurrency(reportData.current.assets.current_assets.inventory)}
                        />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.assets.current_assets.inventory)}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                        Other Current Assets
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(
                          reportData.current.assets.current_assets.other_current_assets
                        )}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.assets.current_assets.other_current_assets
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr className="font-semibold">
                      <td className="px-6 py-3 text-sm text-gray-900">Total Current Assets</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        <BlurredValue
                          value={formatCurrency(reportData.current.assets.current_assets.total)}
                        />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.assets.current_assets.total)}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>

                    {/* Fixed Assets */}
                    <tr className="bg-gray-50">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-2 text-sm font-semibold text-gray-900"
                      >
                        Fixed Assets
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                        Property and Equipment
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.current.assets.fixed_assets.gross_fixed_assets)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.assets.fixed_assets.gross_fixed_assets
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                        Less: Accumulated Depreciation
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        (
                        {formatCurrency(
                          Math.abs(reportData.current.assets.fixed_assets.accumulated_depreciation)
                        )}
                        )
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            (
                            {formatCurrency(
                              Math.abs(
                                reportData.comparison.assets.fixed_assets.accumulated_depreciation
                              )
                            )}
                            )
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr className="font-semibold">
                      <td className="px-6 py-3 text-sm text-gray-900">Net Fixed Assets</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.current.assets.fixed_assets.net_fixed_assets)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.assets.fixed_assets.net_fixed_assets
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>

                    {/* Total Assets */}
                    <tr className="font-bold bg-emerald-50 border-t-2 border-gray-300">
                      <td className="px-6 py-4 text-base text-gray-900">TOTAL ASSETS</td>
                      <td className="px-6 py-4 text-base text-right text-emerald-600">
                        <BlurredValue value={formatCurrency(reportData.current.assets.total)} />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-base text-right text-gray-500">
                            {formatCurrency(reportData.comparison.assets.total)}
                          </td>
                          <td className="px-6 py-4 text-base text-right">
                            {renderVariance(reportData.variance?.total_assets)}
                          </td>
                        </>
                      )}
                    </tr>

                    {/* LIABILITIES */}
                    <tr className="bg-gray-100">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-3 text-sm font-bold text-gray-900"
                      >
                        LIABILITIES
                      </td>
                    </tr>

                    {/* Current Liabilities */}
                    <tr className="bg-gray-50">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-2 text-sm font-semibold text-gray-900"
                      >
                        Current Liabilities
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">Accounts Payable</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(
                          reportData.current.liabilities.current_liabilities.accounts_payable
                        )}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.liabilities.current_liabilities.accounts_payable
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                        Credit Cards Payable
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(
                          reportData.current.liabilities.current_liabilities.credit_cards
                        )}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.liabilities.current_liabilities.credit_cards
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">Accrued Expenses</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(
                          reportData.current.liabilities.current_liabilities.accrued_expenses
                        )}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.liabilities.current_liabilities.accrued_expenses
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr className="font-semibold">
                      <td className="px-6 py-3 text-sm text-gray-900">Total Current Liabilities</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.current.liabilities.current_liabilities.total)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.liabilities.current_liabilities.total
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>

                    {/* Long-term Liabilities */}
                    <tr className="bg-gray-50">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-2 text-sm font-semibold text-gray-900"
                      >
                        Long-term Liabilities
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">Long-term Debt</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.current.liabilities.long_term_liabilities)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.liabilities.long_term_liabilities
                            )}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>

                    {/* Total Liabilities */}
                    <tr className="font-bold bg-gray-50 border-t-2 border-gray-300">
                      <td className="px-6 py-4 text-base text-gray-900">TOTAL LIABILITIES</td>
                      <td className="px-6 py-4 text-base text-right text-gray-900">
                        {formatCurrency(reportData.current.liabilities.total)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-base text-right text-gray-500">
                            {formatCurrency(reportData.comparison.liabilities.total)}
                          </td>
                          <td className="px-6 py-4 text-base text-right">
                            {renderVariance(reportData.variance?.total_liabilities)}
                          </td>
                        </>
                      )}
                    </tr>

                    {/* EQUITY */}
                    <tr className="bg-gray-100">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-3 text-sm font-bold text-gray-900"
                      >
                        EQUITY
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">Owner's Capital</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.current.equity.owners_capital)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.equity.owners_capital)}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">Retained Earnings</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.current.equity.retained_earnings)}
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.equity.retained_earnings)}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                        Current Year Earnings
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        <BlurredValue
                          value={formatCurrency(reportData.current.equity.current_year_earnings)}
                        />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-3 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.equity.current_year_earnings)}
                          </td>
                          <td className="px-6 py-3 text-sm text-right"></td>
                        </>
                      )}
                    </tr>

                    {/* Total Equity */}
                    <tr className="font-bold bg-gray-50 border-t-2 border-gray-300">
                      <td className="px-6 py-4 text-base text-gray-900">TOTAL EQUITY</td>
                      <td className="px-6 py-4 text-base text-right text-gray-900">
                        <BlurredValue value={formatCurrency(reportData.current.equity.total)} />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-base text-right text-gray-500">
                            {formatCurrency(reportData.comparison.equity.total)}
                          </td>
                          <td className="px-6 py-4 text-base text-right">
                            {renderVariance(reportData.variance?.total_equity)}
                          </td>
                        </>
                      )}
                    </tr>

                    {/* Total Liabilities + Equity */}
                    <tr className="font-bold bg-emerald-50 border-t-2 border-gray-300">
                      <td className="px-6 py-4 text-base text-gray-900">
                        TOTAL LIABILITIES + EQUITY
                      </td>
                      <td className="px-6 py-4 text-base text-right text-emerald-600">
                        <BlurredValue
                          value={formatCurrency(
                            reportData.current.liabilities.total + reportData.current.equity.total
                          )}
                        />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-base text-right text-gray-500">
                            {formatCurrency(
                              reportData.comparison.liabilities.total +
                                reportData.comparison.equity.total
                            )}
                          </td>
                          <td className="px-6 py-4 text-base text-right"></td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Report Footer */}
            <div className="mt-6 text-sm text-gray-500 print:block">
              <p>Generated on {new Date().toLocaleString('en-NG')}</p>
              <p>Report run by: {user?.email}</p>
              <p className="text-emerald-600 font-medium">Wholesale Module Report</p>
            </div>
          </>
        )}

        {/* Print Styles */}
        <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .p-4, .p-4 * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
      </div>
    </div>
  );
};

export default WholesaleBalanceSheetPage;
